<template>
  <div
    class="sn-currency"
  >
    <v-text-field
      :id="snInputProps_identifier"
      v-model="response"
      :autofocus="snInputProps_autofocus"
      :data-cy="snInputProps_identifier + '-field'"
      :class="snInputProps_classes"
      :error-messages="snInputProps_errorMessages"
      :placeholder="snInputProps_disabled ? null : '0.00'"
      :hint="snInputProps_hint"
      :required="snInputProps_required"
      :disabled="snInputProps_disabled"
      :aria-label="snInputProps_label"
      :maxlength="snInputProps_maxLength <= 0 ? undefined : snInputProps_maxLength"
      :type="snInputProps_type"
      :rules="snInputProps_rules"
      :validate-on-blur="snInputProps_validateOnBlur"
      inputmode="decimal"
      min="0"
      prepend-inner-icon="sn-icon-dollar-sign"
      autocomplete="nope"
      outlined
      @keypress="filterOutNonNumeric()"
      @keydown="$emit('keydown')"
      @keyup="$emit('keyup')"
      @blur="$emit('blur'); validate(true, true)"
      @focus="$emit('focus'); removeNumberFormatting()"
    />
  </div>
</template>

<script>
import SnInputProps from '../../props/SnInputProps'
import _ from 'lodash'
import deprecatedMixin from '~/components/snui/v2/_deprecated/deprecatedMixin.vue'

export default {
  name: 'SnCurrencyDeprecated',
  mixins: [deprecatedMixin],
  props: {
    ...SnInputProps,
    value: {
      type: [String, Number, Array],
      default: '',
      required: false
    }
  },
  data () {
    return {
      response: '',
      finalResponse: '',
      deprecatedMixin: {
        alternativeComponent: 'SnCurrency'
      }
    }
  },
  watch: {
    value (newVal) {
      if (!this.response) {
        this.response = newVal
      }
      const response = _.clone(this.response)

      if (!response) return

      if (newVal && newVal !== parseFloat(response.replace(/[^\d.-]/g, '')).toFixed(2)) {
        let value = _.clone(newVal)
        if (value && value !== undefined) {
          // ensure the value is a string
          value = value.toString()
          if (value.includes('$')) value = value.replace(/\$/g, '').replace(/^[ \t]+/, '')
          value = this.validate(false, false, value)
        }
        this.response = value
      } else if (!newVal || newVal.length === 0) {
        this.response = newVal
      }
    },
    response (newVal) {
      if (newVal) {
        const response = parseFloat(_.clone(newVal).replace(/[^\d.-]/g, '')).toFixed(2)
        this.$emit('input', response)
      } else if (!newVal || newVal.length === 0) {
        this.$emit('input', newVal)
      }
    }
  },
  created () {
    let value = _.clone(this.value)
    if (value && value !== undefined) {
      // ensure the value is a string
      value = value.toString()
      if (value.includes('$')) value = value.replace(/\$/g, '').replace(/^[ \t]+/, '')
      value = this.validate(false, true, value)
    }
    this.response = value
  },
  methods: {
    removeNumberFormatting () {
      // en-US is the locale used later in validate for final number formatting, so this assumes US number formatting and removes the comma.
      // This should be updated if we start supporting other locales
      this.response = this.response?.replace(/,/g, '')
    },
    validate (setResponse = true, setDecimal = false, value = _.clone(this.response)) {
      if (value) {
        let newVal = value.replace(/[^\d.]/g, '')
        if (setDecimal) {
          if (value.indexOf('.') > 0) {
            // make sure we truncate to 2 decimal places so rounding does not occur
            newVal = value.slice(0, (value.indexOf('.')) + 3).replace(/[^\d.]/g, '')
          }
          value = parseFloat(newVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        } else {
          const valArr = newVal.split('.')
          valArr[0] = parseFloat(valArr[0]).toLocaleString('en-US')
          value = valArr.join('.')
        }

        if (setResponse) {
          this.response = value
        } else {
          return value
        }
        this.finalResponse = this.response
      }
    },
    filterOutNonNumeric (evt = window.event) {
      const expect = evt.target.value.toString() + evt.key.toString()

      if (!/^[0-9]([0-9,.]+)?$/.test(expect.replaceAll(',', ''))) {
        evt.preventDefault()
      } else if (/(\.)(?=.*\1)/g.test(expect)) {
        // prevent multiple decimal points
        evt.preventDefault()
      } else {
        return true
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';

.sn-currency {
  .v-input--is-disabled {
    ::v-deep .v-input__slot {
      background-color: $sn-gray-01;
      border: 1px solid $sn-gray-04;
      .v-input__prepend-inner {
        .v-icon {
          color: $sn-primary-01 !important;
        }
      }
      .v-text-field__slot {
        input {
          color: $sn-primary-01 !important;
        }
      }
    }
  }
  ::v-deep {
    font-family: $font-family-primary !important;

    input {
      // fixes safari input clipping
      height: initial !important;
    }

    .v-input__slot {
      background-color: $sn-white;
      border: 1px solid $sn-gray-04;
      box-sizing: border-box;
      min-height: 48px !important;
      border-radius: 2px;
      margin: 0 !important;

      & fieldset {
        border: none;
      }

      &:focus-within {
        border: 1px solid $sn-primary !important;
      }

      .v-text-field__slot {
        min-height: 46px !important;
      }

      .v-input__prepend-inner {
        margin-top: 12px;

        .v-icon {
          font-family: 'open sans' !important;
          font-size: 16px;
          color: $sn-primary !important;
        }
      }
    }
  }
  .error--text {
    ::v-deep .v-input__slot {
      border-color: $sn-error;
    }
  }
  ::v-deep .v-text-field__details {
    padding-left: 0 !important;
  }
  ::v-deep .v-messages {
    margin: 8px 0 0 0;
    min-height: 22px;

    .v-messages__message {
      @extend .sn-body-2;
      color: $sn-primary-01;
      display: flex;
      align-content: center;
      line-height: inherit;

      &.message-transition-leave-active, &.message-transition-leave-to {
        display: none;
      }
    }

    &.error--text .v-messages__message {
      color: $sn-error;
      &:before {
        font-family: SimpleNexusIcons !important;
        font-size: 18px;
        width: 24px;
        text-align: center;
        content: "\e972";
        margin-right: 4px;
      }
    }

    &.error--text .v-messages__wrapper {
      display: inline-flex;
      padding-bottom: 2px;
    }
  }
  ::v-deep .v-text-field__slot {
    input {
      caret-color: $sn-primary;
    }
    input::placeholder {
      color: $sn-primary-01 !important;
    }
  }
}
</style>
